import { Fragment, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Eye } from "lucide-react";
import { api, errMsg } from "@/lib/api";

const EMPTY = { day: 1, challenge_number: 1, prompt: "", options: ["", "", "", ""], correct_index: 0, points: 100, explanation: "", status: "draft" };
const DEFAULT_POINTS = { 1: 100, 2: 300, 3: 600 };
const STATUS_STYLE = { draft: "bg-slate-800 text-slate-300", scheduled: "bg-amber-500/20 text-amber-300", published: "bg-emerald-500/20 text-emerald-300", archived: "bg-slate-800 text-slate-500" };

function toBody(form) {
  return { day: +form.day, challenge_number: +form.challenge_number, prompt: form.prompt, options: form.options, correct_index: +form.correct_index, points: +form.points, explanation: form.explanation || null, status: form.status };
}

function WeekGrid({ list, totalDays, onPick, onNew, activeDay }) {
  const days = Array.from({ length: totalDays }, (_, i) => i + 1);
  return (
    <div className="panel p-3" data-testid="admin-week-grid">
      <p className="eyebrow mb-2 px-1">{totalDays}-day plan · tap a slot</p>
      <div className="grid grid-cols-4 gap-1 text-center max-h-[420px] overflow-y-auto">
        <span className="eyebrow py-1">Day</span>{[1, 2, 3].map((n) => <span key={n} className="eyebrow py-1">Ch {n}</span>)}
        {days.map((d) => (
          <Fragment key={d}>
            <button type="button" onClick={() => onPick(d)} className={`rounded-md py-2 font-mono text-xs font-bold ${activeDay === d ? "bg-white text-slate-900" : "bg-slate-800 text-slate-200 hover:bg-slate-700"}`} data-testid={`admin-grid-day-${d}`}>{d}</button>
            {[1, 2, 3].map((n) => {
              const q = list.find((x) => x.day === d && x.challenge_number === n && x.status === "published") || list.find((x) => x.day === d && x.challenge_number === n && x.status !== "archived");
              return q ? (
                <span key={n} className={`rounded-md py-2 font-mono text-[10px] uppercase ${STATUS_STYLE[q.effective_status]}`} data-testid={`admin-grid-slot-${d}-${n}`}>{q.effective_status === "published" ? "live" : q.effective_status}</span>
              ) : (
                <button key={n} type="button" onClick={() => onNew(d, n)} className="rounded-md border border-dashed border-slate-700 py-2 text-xs text-slate-500 hover:border-slate-500 hover:text-white" data-testid={`admin-grid-slot-${d}-${n}`}>+</button>
              );
            })}
          </Fragment>
        ))}
      </div>
    </div>
  );
}

function Preview({ form }) {
  return (
    <div className="rounded-xl border border-dashed border-slate-600 p-4" data-testid="admin-q-preview">
      <p className="eyebrow mb-2">Player preview · Day {form.day} · Challenge {form.challenge_number} of 3</p>
      <div className="panel p-5">
        <p className="eyebrow mb-2 text-amber-400">Worth {form.points} pts</p>
        <p className="whitespace-pre-line font-display text-lg font-semibold">{form.prompt || "(empty prompt)"}</p>
      </div>
      <div className="mt-3 grid gap-2">
        {form.options.map((o, i) => (
          <div key={i} className="flex items-center gap-3 rounded-xl border border-slate-800 bg-[#111827] px-4 py-3"><span className="grid h-7 w-7 place-items-center rounded-lg bg-slate-800 font-mono text-xs font-bold">{"ABCD"[i]}</span><span className="text-sm">{o || <em className="text-slate-500">empty</em>}</span></div>
        ))}
      </div>
      <p className="mt-2 text-xs text-slate-500">Players never see the correct answer ({"ABCD"[form.correct_index]}) or the explanation.</p>
    </div>
  );
}

export function AdminQuestions() {
  const [list, setList] = useState([]);
  const [form, setForm] = useState(null);
  const [preview, setPreview] = useState(false);
  const [busy, setBusy] = useState(false);
  const [day, setDay] = useState(null);
  const [totalDays, setTotalDays] = useState(7);

  const load = () => api.get("/admin/questions").then(({ data }) => setList(data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); api.get("/admin/season").then(({ data }) => { setTotalDays(data.total_days); setDay((d) => d ?? Math.max(1, data.day)); }).catch(() => {}); }, []);

  const edit = (q) => { setPreview(false); setForm({ ...q, explanation: q.explanation ?? "" }); };
  const newFor = (d, n) => { setPreview(false); setForm({ ...EMPTY, day: d, challenge_number: n, points: DEFAULT_POINTS[n] }); };
  const setOpt = (i, v) => setForm({ ...form, options: form.options.map((o, j) => (j === i ? v : o)) });

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (form.id) await api.put(`/admin/questions/${form.id}`, toBody(form)); else await api.post("/admin/questions", toBody(form));
      toast.success("Question saved"); setForm(null); load();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const remove = async (q) => {
    if (!window.confirm(`Delete draft Day ${q.day} · Challenge ${q.challenge_number}?`)) return;
    try { await api.delete(`/admin/questions/${q.id}`); toast.success("Deleted"); load(); } catch (err) { toast.error(errMsg(err)); }
  };

  const setStatus = async (q, status) => {
    try { await api.put(`/admin/questions/${q.id}`, toBody({ ...q, status })); toast.success(status === "published" ? "Published" : "Unpublished"); load(); } catch (err) { toast.error(errMsg(err)); }
  };

  if (form) {
    return (
      <form onSubmit={save} className="panel space-y-4 p-5" data-testid="admin-question-form">
        <h3 className="font-display text-lg font-semibold">{form.id ? "Edit question" : "New question"}</h3>
        <div className="grid grid-cols-3 gap-3">
          <label className="block"><span className="eyebrow mb-1 block">Day</span><input className="field" type="number" min={1} max={30} value={form.day} onChange={(e) => setForm({ ...form, day: e.target.value })} data-testid="admin-q-day-input" /></label>
          <label className="block"><span className="eyebrow mb-1 block">Challenge</span><input className="field" type="number" min={1} max={3} value={form.challenge_number} onChange={(e) => setForm({ ...form, challenge_number: e.target.value, points: DEFAULT_POINTS[e.target.value] ?? form.points })} data-testid="admin-q-number-input" /></label>
          <label className="block"><span className="eyebrow mb-1 block">Points</span><input className="field" type="number" min={0} value={form.points} onChange={(e) => setForm({ ...form, points: e.target.value })} data-testid="admin-q-points-input" /></label>
        </div>
        <label className="block"><span className="eyebrow mb-1 block">Prompt</span><textarea className="field min-h-[96px]" value={form.prompt} onChange={(e) => setForm({ ...form, prompt: e.target.value })} required data-testid="admin-q-prompt-input" /></label>
        <div className="space-y-2">
          <span className="eyebrow block">Options · tap the letter to mark correct</span>
          {form.options.map((o, i) => (
            <div key={i} className="flex items-center gap-2">
              <button type="button" onClick={() => setForm({ ...form, correct_index: i })} className={`grid h-11 w-11 shrink-0 place-items-center rounded-lg font-mono font-bold ${form.correct_index === i ? "bg-emerald-500 text-slate-950" : "bg-slate-800 text-slate-300"}`} data-testid={`admin-q-correct-${i}`}>{"ABCD"[i]}</button>
              <input className="field" value={o} onChange={(e) => setOpt(i, e.target.value)} required={form.status === "published"} data-testid={`admin-q-option-${i}-input`} />
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 gap-3">
          <label className="block"><span className="eyebrow mb-1 block">Status</span>
            <select className="field" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="admin-q-status-select">
              <option value="draft">Draft</option>
              <option value="published">Published / Scheduled</option>
              <option value="archived">Archived</option>
            </select>
          </label>
        </div>
        <label className="block"><span className="eyebrow mb-1 block">Explanation (admin only, never shown to players)</span><textarea className="field min-h-[64px]" value={form.explanation} onChange={(e) => setForm({ ...form, explanation: e.target.value })} data-testid="admin-q-explanation-input" /></label>
        {form.submission_count > 0 && <p className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-300" data-testid="admin-q-locked-note">{form.submission_count} players have answered — options, answer, points, day and challenge number are locked.</p>}
        <button type="button" className="btn-secondary w-full" onClick={() => setPreview(!preview)} data-testid="admin-q-preview-button"><Eye size={16} /> {preview ? "Hide preview" : "Preview as player"}</button>
        {preview && <Preview form={form} />}
        <div className="flex gap-3">
          <button type="button" className="btn-secondary flex-1" onClick={() => setForm(null)} data-testid="admin-q-cancel-button">Cancel</button>
          <button className="btn-primary flex-1" disabled={busy} data-testid="admin-q-save-button">{busy ? "Saving…" : form.status === "published" ? "Save & publish" : "Save"}</button>
        </div>
      </form>
    );
  }

  const visible = day ? list.filter((q) => q.day === day) : list;
  return (
    <div className="space-y-4 md:grid md:grid-cols-12 md:items-start md:gap-6 md:space-y-0">
      <div className="space-y-4 md:col-span-5 lg:col-span-4">
      <WeekGrid list={list} totalDays={totalDays} activeDay={day} onPick={(d) => setDay(d === day ? null : d)} onNew={newFor} />
      <button className="btn-primary w-full" onClick={() => newFor(day || 1, 1)} data-testid="admin-add-question-button"><Plus size={18} /> Add question{day ? ` for Day ${day}` : ""}</button>
      </div>
      <div className="space-y-3 md:col-span-7 lg:col-span-8">
      <p className="eyebrow" data-testid="admin-question-filter-label">{day ? `Day ${day} · ${visible.length} questions` : `All days · ${list.length} questions`}</p>
      <ul className="space-y-2" data-testid="admin-question-list">
        {visible.map((q) => (
          <li key={q.id} className="panel p-4" data-testid={`admin-question-${q.day}-${q.challenge_number}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="eyebrow">Day {q.day} · Ch {q.challenge_number} · {q.points} pts{q.submission_count ? ` · ${q.submission_count} answers` : ""}</p>
                <p className="mt-1 truncate text-sm text-slate-200">{q.prompt.split("\n")[0]}</p>
                <p className="mt-1 text-xs text-emerald-400">Answer: {"ABCD"[q.correct_index]} · {q.options[q.correct_index]}</p>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <span className={`rounded-md px-2 py-1 font-mono text-[10px] uppercase ${STATUS_STYLE[q.effective_status]}`} data-testid={`admin-q-status-${q.id}`}>{q.effective_status}</span>
                <button onClick={() => edit(q)} className="rounded-md p-2 text-slate-400 hover:bg-slate-800 hover:text-white" data-testid={`admin-q-edit-${q.id}`} aria-label="Edit"><Pencil size={15} /></button>
                {q.status === "draft" && <button onClick={() => remove(q)} className="rounded-md p-2 text-slate-400 hover:bg-slate-800 hover:text-rose-400" data-testid={`admin-q-delete-${q.id}`} aria-label="Delete"><Trash2 size={15} /></button>}
              </div>
            </div>
            <div className="mt-3 flex gap-2">
              {q.status === "published"
                ? <button onClick={() => setStatus(q, "draft")} className="rounded-lg border border-slate-700 px-3 py-1.5 text-xs font-medium text-slate-200 hover:bg-slate-800" data-testid={`admin-q-unpublish-${q.id}`}>Unpublish</button>
                : <button onClick={() => setStatus(q, "published")} className="rounded-lg bg-emerald-500 px-3 py-1.5 text-xs font-semibold text-slate-950 hover:bg-emerald-400" data-testid={`admin-q-publish-${q.id}`}>Publish</button>}
            </div>
          </li>
        ))}
      </ul>
      </div>
    </div>
  );
}
